import { useMemo, useState, useEffect } from "react";

import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { FloatLabel } from "primereact/floatlabel";
import { ValidationMessage } from "sj-ui-toolkit/src/components";

import { validatePathName } from "@/api/paths";
import type { Level } from "@/api/levels";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DetailsValues {
  name: string;
  description: string;
  levelId: string | number;
  levelName: string;
  rankName: string;
}

interface ValidationError {
  hasError: boolean;
  message: string;
}

interface PathDetailsStepProps {
  values: DetailsValues;
  handleChange: (newValues: DetailsValues) => void;
  showValidationMessages: boolean;
  validationErrors: {
    name?: ValidationError;
    description?: ValidationError;
    level?: ValidationError;
  };
  isLoading?: boolean;
  contentTypeLabel: string;
  existingPathNames?: string[];
  currentPathName?: string | null;
  isNew: boolean;
  levels: Level[];
  levelsLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PathDetailsStep = ({
  values,
  handleChange,
  showValidationMessages,
  validationErrors,
  isLoading = false,
  contentTypeLabel,
  existingPathNames = [],
  currentPathName = null,
  levels = [],
  levelsLoading = false,
}: PathDetailsStepProps) => {
  const [nameError, setNameError] = useState({ isValid: true, message: "" });
  const [descriptionError, setDescriptionError] = useState({ isValid: true, message: "" });
  const [uniqueNameSuggestion, setUniqueNameSuggestion] = useState<string | null>(null);

  const levelOptions = useMemo(
    () =>
      levels.map(level => ({
        label: level.name,
        value: level.rankName,
        levelId: level.id,
      })),
    [levels]
  );

  const handleFieldChange = (field: string, value: string) => {
    if (field === "level") {
      const selectedLevel = levels.find(l => l.rankName === value);
      handleChange({
        ...values,
        levelId: selectedLevel ? selectedLevel.id : "",
        levelName: selectedLevel ? selectedLevel.name : "",
        rankName: value,
      });
    } else {
      handleChange({ ...values, [field]: value });
    }
  };

  const handleSuggestionClick = () => {
    if (uniqueNameSuggestion) handleFieldChange("name", uniqueNameSuggestion);
  };

  useEffect(() => {
    if (values.name) {
      const validation = validatePathName(values.name, existingPathNames, currentPathName);
      setNameError({ isValid: validation.isValid, message: validation.message });
      setUniqueNameSuggestion(validation.suggestion ?? null);
    }
  }, [values.name, existingPathNames, currentPathName]);

  useEffect(() => {
    const trimmed = values.description ? values.description.trim() : "";
    if (trimmed) {
      const isLengthError = trimmed.length > 500;
      setDescriptionError({
        isValid: !isLengthError,
        message: isLengthError
          ? `${contentTypeLabel} description must be 500 characters or less`
          : "",
      });
    } else {
      setDescriptionError({ isValid: true, message: "" });
    }
  }, [values.description, contentTypeLabel]);

  const isNameInvalid =
    !nameError.isValid || (showValidationMessages && !!validationErrors.name?.hasError);
  const nameErrorSeverity = !nameError.isValid ? "warn" : "error";
  const nameErrorText = !nameError.isValid ? (
    uniqueNameSuggestion ? (
      <span>
        A path with this name already exists. Try:{" "}
        <span className="cursor-pointer underline font-bold" onClick={handleSuggestionClick}>
          &ldquo;{uniqueNameSuggestion}&rdquo;
        </span>
      </span>
    ) : (
      nameError.message
    )
  ) : (
    validationErrors.name?.message
  );

  const isDescriptionInvalid =
    !descriptionError.isValid ||
    (showValidationMessages && !!validationErrors.description?.hasError);
  const descriptionErrorSeverity = !descriptionError.isValid ? "warn" : "error";
  const descriptionErrorText = !descriptionError.isValid
    ? descriptionError.message
    : validationErrors.description?.message;

  return (
    <>
      <div className="mt-10">
        <FloatLabel>
          <InputText
            id="name"
            value={values.name || ""}
            autoComplete="off"
            onChange={e => handleFieldChange("name", e.target.value)}
            className="w-full"
            aria-required="true"
            aria-invalid={isNameInvalid}
            aria-describedby={isNameInvalid ? "path-name-error" : undefined}
            invalid={!nameError.isValid}
            disabled={isLoading}
          />
          <label htmlFor="name">{contentTypeLabel} Name</label>
        </FloatLabel>
        {isNameInvalid && (
          <div className="mt-2" role="alert">
            <ValidationMessage
              severity={nameErrorSeverity}
              id="path-name-error"
              text={nameErrorText}
            />
          </div>
        )}
      </div>

      <div className="mt-10">
        <FloatLabel>
          <InputTextarea
            id="description"
            value={values.description || ""}
            onChange={e => handleFieldChange("description", e.target.value)}
            rows={5}
            autoComplete="off"
            className="w-full"
            aria-required="true"
            aria-invalid={isDescriptionInvalid}
            aria-describedby={isDescriptionInvalid ? "path-description-error" : undefined}
            invalid={!descriptionError.isValid}
            disabled={isLoading}
          />
          <label htmlFor="description">{contentTypeLabel} Description</label>
        </FloatLabel>
        {isDescriptionInvalid && (
          <div className="mt-2" role="alert">
            <ValidationMessage
              severity={descriptionErrorSeverity}
              text={descriptionErrorText}
              id="path-description-error"
            />
          </div>
        )}
      </div>

      {contentTypeLabel === "Path" && (
        <div className="mt-10">
          <FloatLabel>
            <Dropdown
              id="level"
              value={values.rankName}
              onChange={e => handleFieldChange("level", e.value as string)}
              loading={isLoading || levelsLoading}
              options={levelOptions}
              className="w-full"
              disabled={isLoading || levelsLoading}
              aria-label="Level"
            />
            <label htmlFor="level">Level</label>
          </FloatLabel>
          {showValidationMessages && validationErrors.level?.hasError && (
            <div className="mt-2" role="alert">
              <ValidationMessage
                severity="error"
                text={validationErrors.level?.message ?? "Level is required"}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default PathDetailsStep;
